import {gql} from '@apollo/client'
import React, {ReactNode} from 'react'
import {Routes} from 'react-router-dom'
import styled from 'styled-components'
import {useSetCrumbs} from '../../../components/crumbtrail'
import {
  PageBody,
  PageHeader,
  PageHeaderContent
} from '../../../components/page-styles'
import {Icon} from '../../../components/library/icon/icon'
import {TabLink, TabNav} from '../../../components/library/tab/tab'
import {
  PermissionAction,
  PermissionResource
} from '../../../data/workbench-api/generated/types'
import {PrivateRoute, Route} from '../../../lib/router'
import {OrganisationNode} from '../../../organisation'
import {IconHeading, InfoBox} from '../../configuration/components'
import {usePermissionOverviewQuery} from './generated'
import {Groups} from './groups'
import {Roles} from './roles'
import {WorkbenchMembers} from './workbench-members'

export const PermissionOverview = ({
  organisation
}: {
  organisation: OrganisationNode
}) => {
  const {data} = usePermissionOverviewQuery({
    variables: {organisationId: organisation.id}
  })

  useSetCrumbs(['Workbench members'])

  return (
    <>
      <PageHeader>
        <PageHeaderContent>
          <h2>Workbench members</h2>
        </PageHeaderContent>
      </PageHeader>

      <InfoBox>
        <IconHeading>
          <Icon name="profile" />
          Overview
        </IconHeading>
        <ul>
          <li>
            <h6>Organisation</h6>
            <p>{organisation.name}</p>
          </li>
          <li>
            <h6>Members</h6>
            <p>{data?.query.businessUsers?.totalCount || null}</p>
          </li>
          <li>
            <h6>Groups</h6>
            <p>{data?.query.groups?.totalCount || null}</p>
          </li>
          <li>
            <h6>Roles</h6>
            <p>{data?.query.customRoles?.totalCount || null}</p>
          </li>
        </ul>
      </InfoBox>

      <TabNav>
        <TabLink to="">Workbench members</TabLink>
        <TabLink to="groups">Groups</TabLink>
        <TabLink to="roles">Roles</TabLink>
      </TabNav>
      <Routes>
        <Route
          path="/"
          render={() => <WorkbenchMembers organisation={organisation} />}
        />
        <PrivateRoute
          path="/groups"
          permissionCheck={[PermissionResource.Group, PermissionAction.View]}
          render={() => <Groups organisation={organisation} />}
        />
        <PrivateRoute
          path="/roles"
          permissionCheck={[
            PermissionResource.CustomRole,
            PermissionAction.View
          ]}
          render={() => <Roles organisation={organisation} />}
        />
      </Routes>
    </>
  )
}

gql`
  query PermissionOverview($organisationId: String!) {
    query {
      businessUsers(first: 0, condition: {organisationId: $organisationId}) {
        totalCount
      }
      groups(first: 0, condition: {organisationId: $organisationId}) {
        totalCount
      }
      customRoles(first: 0, condition: {organisationId: $organisationId}) {
        totalCount
      }
    }
  }
`

export const ListHeader = styled.div`
  display: flex;
  margin: 0 0 30px;
  align-items: flex-start;
  justify-content: space-between;
  p {
    max-width: 80%;
    color: ${p => p.theme.grey80};
    margin: 0;
  }
`

export const ListWrapper = styled.div`
  background: ${p => p.theme.grey05};
  border-radius: ${p => p.theme.borderRadius};
  margin: 0 0 30px;
  padding: 10px 20px 20px;
`

export const ListLayout = ({
  header,
  children
}: {
  header: ReactNode
  children: ReactNode
}) => {
  return (
    <PageBody>
      <ListHeader>{header}</ListHeader>
      <ListWrapper>{children}</ListWrapper>
    </PageBody>
  )
}
